import { useCallback } from 'react';
import type { RefCallback } from 'react';

import { getAvoidElements, subscribeAvoidElements } from './avoid-registry';

const LIFT_VARIABLE = '--notification-lift';

/**
 * How far above the bottom of the window the stack must start, in px: the top of the highest registered control
 * that is visible, shares a column with the stack and is pinned to the lower half of the window. Zero when none is.
 */
function measureLift(stack: HTMLElement): number {
  const column = stack.getBoundingClientRect();
  const windowHeight = document.documentElement.clientHeight;
  let top = windowHeight;

  for (const element of getAvoidElements()) {
    const rect = element.getBoundingClientRect();
    const visible = rect.width > 0 && rect.height > 0;
    const sharesColumn = rect.right > column.left && rect.left < column.right;
    const pinnedToBottom = rect.bottom > windowHeight / 2 && rect.top < windowHeight;
    if (visible && sharesColumn && pinnedToBottom) top = Math.min(top, rect.top);
  }

  return Math.max(0, Math.ceil(windowHeight - top));
}

/**
 * A ref for the stack. While the stack is on the page, it keeps `--notification-lift` current as registered
 * controls appear, change size and go away. A size change is measured before the next paint. Window events wait
 * for the next frame.
 */
export function useStackLift(): RefCallback<HTMLElement> {
  return useCallback((stack: HTMLElement | null) => {
    if (!stack) return undefined;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const lift = measureLift(stack);
      if (lift) stack.style.setProperty(LIFT_VARIABLE, `${lift}px`);
      else stack.style.removeProperty(LIFT_VARIABLE);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    const observeAll = () => {
      if (!observer) return;
      observer.disconnect();
      observer.observe(stack);
      getAvoidElements().forEach((element) => observer.observe(element));
    };

    observeAll();
    measure();
    const unsubscribe = subscribeAvoidElements(() => {
      observeAll();
      measure();
    });
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    document.addEventListener('transitionend', schedule, true);

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      unsubscribe();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule);
      document.removeEventListener('transitionend', schedule, true);
    };
  }, []);
}
